import React from "react";
import { Chip, Group, Loader, Text } from "@mantine/core";

export interface ChipOption {
  id: string;
  name: string;
  color?: string;
  description?: string;
}

export interface ChipSelectorProps {
  title: string;
  options: ChipOption[];
  selectedIds: string[];
  onSelectionChange: (optionId: string, isSelected: boolean) => void;
  multiSelect?: boolean;
  helpText?: string;
  loading?: boolean;
  disabled?: boolean;
  variant?: "default" | "colorful";
  size?: "xs" | "sm" | "md" | "lg";
}

const ChipSelector: React.FC<ChipSelectorProps> = ({
  title,
  options,
  selectedIds,
  onSelectionChange,
  multiSelect = true,
  helpText,
  loading = false,
  disabled = false,
  variant = "default",
  size = "sm",
}) => {
  const handleChipChange = (optionId: string) => {
    const isSelected = selectedIds.includes(optionId);

    if (!multiSelect && !isSelected) {
      // For single select, deselect all others first
      selectedIds.forEach((selectedId) => {
        if (selectedId !== optionId) {
          onSelectionChange(selectedId, false);
        }
      });
    }

    onSelectionChange(optionId, !isSelected);
  };

  const getChipStyle = (option: ChipOption, isSelected: boolean) => {
    const baseStyle = {
      cursor: disabled ? "default" : "pointer",
      borderRadius: "20px",
      opacity: disabled ? 0.6 : 1,
    };

    if (variant === "colorful" && option.color) {
      return {
        ...baseStyle,
        backgroundColor: isSelected ? option.color : undefined,
        borderColor: option.color,
        color: isSelected ? "white" : option.color,
      };
    }

    return baseStyle;
  };

  const generateHelpText = () => {
    if (helpText) return helpText;
    if (options.length === 0) {
      return "Business does not have any tags yet. See business settings to manage tags.";
    }

    if (multiSelect) {
      const selectedCount = selectedIds.length;
      return selectedCount === 0
        ? `No ${title.toLowerCase()} selected. Click chips to select them.`
        : "Click selected chips to remove them or unselected chips to add them.";
    } else {
      return `Click a chip to set the ${title.toLowerCase()}.`;
    }
  };

  return (
    <div>
      <Group gap="xs" mb="xs" align="center">
        <Text size={size} fw={500}>
          {title}
        </Text>
        {loading && <Loader size="xs" />}
      </Group>

      <Group gap="xs">
        {options.map((option) => {
          const isSelected = selectedIds.includes(option.id);

          return (
            <Chip
              key={option.id}
              checked={isSelected}
              onChange={() => !disabled && handleChipChange(option.id)}
              variant={isSelected ? "filled" : "outline"}
              size={size}
              disabled={disabled}
              style={getChipStyle(option, isSelected)}
            >
              {option.name}
            </Chip>
          );
        })}
      </Group>

      <Text size="xs" c="dimmed" mt="xs">
        {generateHelpText()}
      </Text>
    </div>
  );
};

export default ChipSelector;
